import { isEqual, isNil } from 'lodash';
import { carriersConfig } from '~domain/model/configs/carriers-config/carriers-config-facade';
import { Province } from '~domain/model/core/address/province';
import { Carrier } from '~domain/model/core/carrier';
import { ProductType } from '~domain/model/core/product-type';
import { QuotesResultFacade } from '~domain/model/facade/quotes/quotes-result-facade';
import { CarrierQuote } from '~domain/model/quote/quote';

type MobileAppTracking = {
  withoutApp: string;
  withApp: string;
};

const mobileAppTrackingCarrierCodes: Partial<Record<Carrier, Partial<Record<Province, MobileAppTracking>>>> = {
  // Intact my Drive
  [Carrier.Intact]: {
    [Province.Quebec]: {
      withoutApp: 'GC',
      withApp: 'GC3',
    },
    [Province.Ontario]: {
      withoutApp: 'HAL',
      withApp: 'HAL3',
    },
  },
  // Travelers IntelliDrive
  [Carrier.Travelers]: {
    [Province.Ontario]: {
      withoutApp: 'DOC',
      withApp: 'DOC3',
    },
  },
};

/** Determines if a carrier has mobile app tracking carrier codes defined */
export function hasMobileAppTracking(carrier: Carrier): boolean {
  return !isNil(mobileAppTrackingCarrierCodes[carrier]);
}

/** Determines if this carrier code tracks mobile app usage */
export function isTrackingMobileApp(carrierCode: string): boolean {
  const carrier = carriersConfig.getCarrierFromCode(carrierCode);
  const carrierCodesByProvince = mobileAppTrackingCarrierCodes[carrier];

  // mobile app tracking carrier codes are not defined for this carrier
  if (isNil(carrierCodesByProvince)) {
    return false;
  }

  // check that given carrier code matches one of the provinces defined code
  return Object.values(carrierCodesByProvince)
    .map((codes) => codes.withApp)
    .includes(carrierCode);
}

/** Determine if given carrier code has an alternative carrier quote that tracks mobile app  */
export function hasMobileAppTrackingQuote(
  quotesResultFacade: QuotesResultFacade,
  province: Province,
  productType: ProductType,
  carrierCode: string,
): boolean {
  const assetQuotes = quotesResultFacade.getQuotes(productType);
  const oppositeCarrierQuotes = getOppositeMobileAppTrackingQuotes(
    quotesResultFacade,
    productType,
    province,
    carrierCode,
  );

  // every asset for given product should have a carrier quote with mobile app tracking equivalent
  return assetQuotes.length === oppositeCarrierQuotes.length;
}

export function getOppositeMobileAppTrackingCarrierCode(province: Province, carrierCode: string): string | undefined {
  const carrier = carriersConfig.getCarrierFromCode(carrierCode);
  const provinceCodes = mobileAppTrackingCarrierCodes[carrier]?.[province];
  if (isNil(provinceCodes)) {
    return;
  }

  const isWithoutApp = carrierCode === provinceCodes.withoutApp;
  const isWithApp = carrierCode === provinceCodes.withApp;

  // couldn't match carrier code with either code
  if (!isWithoutApp && !isWithApp) {
    return;
  }

  return isWithoutApp ? provinceCodes.withApp : provinceCodes.withoutApp;
}

/** Get carrier quotes with the opposite state of mobile app tracking (carrier quote can track mobile app or not
 * for a discount) */
export function getOppositeMobileAppTrackingQuotes(
  quotesResultFacade: QuotesResultFacade,
  productType: ProductType,
  province: Province,
  carrierCode: string,
): CarrierQuote[] {
  const targetCarrierCode = getOppositeMobileAppTrackingCarrierCode(province, carrierCode);
  if (isNil(targetCarrierCode)) {
    return [];
  }

  return quotesResultFacade.quotes
    .getQuotes({ productTypes: [productType], carrierCode: targetCarrierCode })
    .map(({ assetId, assetQuotes }) => {
      const recommendedCarrierQuote = quotesResultFacade.recommendation.getCarrierQuote(productType, assetId);
      return assetQuotes.find((assetQuote) => isEqual(assetQuote.combinedWith, recommendedCarrierQuote?.combinedWith));
    })
    .filter((q) => !isNil(q));
}
